import {
  ScrollArea,
  Group,
  Stack,
  Text,
  Button,
  Paper,
  ActionIcon,
  Avatar,
  Tooltip,
  ThemeIcon,
  Divider,
  Box,
} from '@mantine/core';
import { IconChevronLeft, IconChevronRight, IconX } from '@tabler/icons-react';
import { AppIcon } from '../common/AppIcon';
import { NavItem } from './NavItem';
import { navSections, currentUser } from '../../data/mockData';
import classes from './Sidebar.module.css';
import { Logo } from '../../../../../layouts/Logo';
import IconWhiteLabel from '../../../../../assets/icons/IconWhiteLabel';

/**
 * Reusable sidebar building blocks — no AppShell dependency.
 * Consumed both by the AppShell layout (Sidebar.jsx) and the standalone
 * demo page (HomeDemo.jsx).
 */

export function WhiteLabelCard({ collapsed }) {
  if (collapsed) {
    return (
      <Tooltip label="Marque blanche" position="right" withArrow>
        <ActionIcon variant="light" color="primary" size={40} radius="md" mx="auto" mt="md" display="block">
          <AppIcon name="palette" size={20} />
        </ActionIcon>
      </Tooltip>
    );
  }

  return (
    <Paper bg="primary.1" radius="lg" p="md" mt="lg">
      <Group gap={8} mb={6}>
        <ThemeIcon variant="subtle" color="primary" size={28} radius="md">
          <IconWhiteLabel defaultFilled='var(--mantine-color-primary-6)' name="palette" size={16} withBackground={false} />
        </ThemeIcon>
        <Text fw={700} fz="md" c="primary.7">
          Marque blanche
        </Text>
      </Group>
      <Text fz="xs" c="primary.9" mb="sm">
        Adaptez Magileads à votre identité visuelle en quelques clics.
      </Text>
      <Button fullWidth size="sm" radius="md" color="primary">
        Créer ma marque blanche
      </Button>
    </Paper>
  );
}

export function SidebarUser({ collapsed }) {
  const initials = currentUser.fullName
    .split(' ')
    .map((n) => n[0])
    .slice(0, 2)
    .join('');

  if (collapsed) {
    return (
      <Tooltip label={currentUser.fullName} position="right" withArrow>
        <Avatar src={currentUser.avatar || undefined} color="primary" radius="xl" size={38} mx="auto">
          {initials}
        </Avatar>
      </Tooltip>
    );
  }

  return (
    <Group justify="space-between" wrap="nowrap">
      <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
        <Avatar src={currentUser.avatar || undefined} color="primary" radius="xl" size={38}>
          {initials}
        </Avatar>
        <div style={{ minWidth: 0 }}>
          <Text fz="sm" fw={600} truncate>
            {currentUser.fullName}
          </Text>
          <Text fz="xs" c="gray.5">
            {currentUser.handle}
          </Text>
        </div>
      </Group>
      <ActionIcon variant="light" color="primary" aria-label="Changer de compte">
        <AppIcon name="swap" size={18} />
      </ActionIcon>
    </Group>
  );
}

/** Logo + collapse (desktop) / close (mobile drawer) controls. */
export function SidebarHeader({ collapsed, onToggleCollapse, showCollapseButton, onCloseMobile }) {
  return (
    <>
      <Group justify={collapsed ? 'center' : 'space-between'} wrap="nowrap">
        <Logo isLoader={collapsed} maw={collapsed ? 64 : 250} />
        {onCloseMobile && (
          <ActionIcon variant="subtle" color="gray" radius="md" onClick={onCloseMobile} aria-label="Fermer le menu">
            <IconX size={18} />
          </ActionIcon>
        )}
        {!onCloseMobile && showCollapseButton && !collapsed && (
          <ActionIcon variant="subtle" color="gray" radius="md" onClick={onToggleCollapse} aria-label="Réduire le menu">
            <IconChevronLeft size={18} />
          </ActionIcon>
        )}
      </Group>
      {!onCloseMobile && showCollapseButton && collapsed && (
        <ActionIcon
          variant="subtle"
          color="gray"
          radius="md"
          mx="auto"
          mt="xs"
          display="block"
          onClick={onToggleCollapse}
          aria-label="Ouvrir le menu"
        >
          <IconChevronRight size={18} />
        </ActionIcon>
      )}
    </>
  );
}

/** The list of navigation sections + the white-label card. */
export function SidebarNavList({ collapsed, onNavigate, active = 'dashboard' }) {
  return (
    <>
      <Stack gap={2}>
        {navSections.map((section) => (
          <div key={section.id}>
            {section.title && !collapsed && <Text className={classes.sectionTitle}>{section.title}</Text>}
            {section.title && collapsed && <Divider my="xs" />}
            {section.items.map((item) => (
              <NavItem
                key={item.id}
                item={item}
                active={active === item.id}
                collapsed={collapsed}
                onClick={onNavigate}
              />
            ))}
          </div>
        ))}
      </Stack>
      <WhiteLabelCard collapsed={collapsed} />
    </>
  );
}

/** Support entry + user block. */
export function SidebarFooter({ collapsed, onNavigate }) {
  return (
    <>
      <Divider mb="sm" />
      <Stack gap="sm">
        <NavItem item={{ id: 'support', label: 'Support', icon: 'support' }} collapsed={collapsed} onClick={onNavigate} />
        <SidebarUser collapsed={collapsed} />
      </Stack>
    </>
  );
}

/**
 * Full sidebar as a plain flex column (header / scrollable middle / footer).
 * Use this anywhere WITHOUT AppShell — e.g. a fixed-width <Box> or a <Drawer>.
 */
export function SidebarContent({
  collapsed = false,
  onToggleCollapse,
  onNavigate,
  showCollapseButton = true,
  onCloseMobile,
}) {
  return (
    <Stack gap={0} h="100%" style={{ flex: 1 }}>
      <Box px="md" pt="md" pb="xs">
        <SidebarHeader
          collapsed={collapsed}
          onToggleCollapse={onToggleCollapse}
          showCollapseButton={showCollapseButton}
          onCloseMobile={onCloseMobile}
        />
      </Box>

      <ScrollArea px="md" py="xs" style={{ flex: 1, minHeight: 0 }}>
        <SidebarNavList collapsed={collapsed} onNavigate={onNavigate} />
      </ScrollArea>

      <Box px="md" pt="xs" pb="md">
        <SidebarFooter collapsed={collapsed} onNavigate={onNavigate} />
      </Box>
    </Stack>
  );
}

export default SidebarContent;
